import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  fetchNotificationsList,
  fetchUnreadCount,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../api/notifications';
import { armForegroundMessages } from '../utils/pushNotifications';

function normalizeList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
}

function formatRelativeTime(dateString) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';

  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 0) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  if (diffHour > 0) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffMin > 0) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  return 'Just now';
}

/**
 * Buyer notifications: HTTP API for history/read-state, FCM push for
 * real-time updates (#101). No polling (#100) — the list refreshes on
 * mount, bell open, tab refocus, and each foreground push.
 * Same system API as dashboard; scoped by the logged-in user's token.
 */
export function useBuyerNotifications({ enabled = true } = {}) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const [listRes, countRes] = await Promise.all([
        fetchNotificationsList({ limit: 30, include_read: true }),
        fetchUnreadCount().catch(() => null),
      ]);

      if (!mountedRef.current) return;

      const list = normalizeList(listRes?.data);
      setNotifications(list);

      if (countRes?.data?.count != null) {
        setUnreadCount(countRes.data.count);
      } else {
        setUnreadCount(list.filter((n) => !n.is_read).length);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err?.message || err?.error || 'Failed to load notifications');
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [enabled]);

  const markAsRead = useCallback(
    async (timestamps) => {
      const ids = Array.isArray(timestamps) ? timestamps : [timestamps];
      if (!ids.length) return;

      await markNotificationAsRead(ids);
      setNotifications((prev) =>
        prev.map((n) =>
          ids.includes(n.timestamp) ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - ids.length));
      await refresh();
    },
    [refresh]
  );

  const markAllAsRead = useCallback(async () => {
    await markAllNotificationsAsRead();
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    await refresh();
  }, [refresh]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    return undefined;
  }, [enabled, refresh]);

  // While the tab is unfocused pushes go to the service worker, so the
  // in-page badge goes stale; resync whenever the tab becomes visible again.
  useEffect(() => {
    if (!enabled) return undefined;

    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, refresh]);

  // Real-time: FCM foreground messages refresh the bell and show a toast
  // (the SDK renders no browser notification while the tab is focused).
  useEffect(() => {
    if (!enabled) return undefined;

    let cleanup = null;
    let cancelled = false;

    armForegroundMessages((payload) => {
      refresh();
      const title = payload?.notification?.title;
      const body = payload?.notification?.body;
      if (title || body) {
        const text = body ? `${title || 'Notification'} — ${body}` : title;
        const redirectUrl = payload?.data?.redirect_url;
        // id dedupes the toast when several bell instances are mounted.
        toast(
          (t) =>
            redirectUrl ? (
              <button
                type="button"
                className="text-left"
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate(
                    redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`
                  );
                }}
              >
                {text}
              </button>
            ) : (
              text
            ),
          {
            icon: '🔔',
            id: payload?.messageId || payload?.fcmMessageId,
          }
        );
      }
    }).then((unsubscribe) => {
      if (cancelled) {
        unsubscribe();
      } else {
        cleanup = unsubscribe;
      }
    });

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, [enabled, refresh, navigate]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    refresh,
    markAsRead,
    markAllAsRead,
    formatRelativeTime,
  };
}
